import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  createComponent,
  EnvironmentInjector,
  ComponentRef,
  inject,
} from '@angular/core';

import * as L from 'leaflet';
import 'leaflet.markercluster';

import { Observable, Subscription, combineLatest, firstValueFrom } from 'rxjs';

import { BrowseFilter } from 'app/modules/browse/models/filter';
import { Sport } from 'app/modules/browse/models/sport';
import { MapService } from 'app/modules/browse/services/map.service';
import { FilterStore } from 'app/modules/browse/store/filter/filter.store';
import { MapStore } from 'app/modules/browse/store/map/map.store';
import { SportStore } from 'app/modules/browse/store/sport/sport.store';
import { ResponsiveService } from 'app/services/responsive.service';
import { LARGE_SCREEN_BREAKPOINT } from 'src/environments/environment.local';
import { SearchedPlaceStore } from 'app/modules/browse/store/searched-place/searched-place.store';
import { Place } from 'app/modules/browse/models/place.interface';
import { TranslateModule } from '@ngx-translate/core';
import { LoaderComponent } from '../../../../../../shared/components/loader/loader.component';
import { NgClass } from '@angular/common';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { LeafletMarkerClusterModule } from '@asymmetrik/ngx-leaflet-markercluster';
import {
  DistancePipe,
  ScenicCardComponent,
  Unit,
  VideoSection,
} from '@kinomap-dev/ng-ui-lib';
import { Store } from '@ngrx/store';
import { selectMeUnit } from 'app/store/me/me.selectors';

const FRANCE_COORDS: L.LatLngLiteral = {
  lat: 46.2276,
  lng: 2.2137,
};
const MIN_ZOOM = 3;
const DEFAULT_ZOOM = 7;
const COUNTRY_ZOOM = 6;
const REGION_ZOOM = 9;
const CITY_ZOOM = 12;

@Component({
  selector: 'app-world-map',
  templateUrl: './world-map.component.html',
  styleUrls: ['./world-map.component.scss'],
  standalone: true,
  imports: [
    LeafletModule,
    LoaderComponent,
    NgClass,
    TranslateModule,
    LeafletMarkerClusterModule,
  ],
})
export class WorldMapComponent implements OnInit, OnDestroy {
  @Input() id!: number;
  @Input() path!: 'playlist' | 'myVideos' | 'videos';

  private responsiveService: ResponsiveService = inject(ResponsiveService);
  private mapService: MapService = inject(MapService);
  private sportStore: SportStore = inject(SportStore);
  private mapStore: MapStore = inject(MapStore);
  private filterStore: FilterStore = inject(FilterStore);
  private searchedPlaceStore: SearchedPlaceStore = inject(SearchedPlaceStore);
  private store: Store = inject(Store);
  private environmentInjector: EnvironmentInjector =
    inject(EnvironmentInjector);

  private subscriptions: Subscription[] = [];
  private centerAndRadius: {
    center: { lat: number; lng: number };
    radius: number;
  } | null = null;
  private sport: Sport | null = null;
  private filters: BrowseFilter[] = [];
  private hasMoved: boolean = false;
  private selectedMarker: L.Marker | null = null;
  private isAutopanInProcess: boolean = false;
  private isLargeScreen: boolean = true;
  private isMovingToAddress: boolean = false;

  videos: VideoSection[] = [];
  totalAvailableVideos: number = 0;
  map: L.Map | null = null;
  mapOptions!: L.MapOptions;
  markers: { [videoId: number]: L.Marker } = {};
  markerClusterGroup: L.MarkerClusterGroup = null;
  isLoading: boolean = false;
  isFull: boolean = false;
  unit$: Observable<Unit> = this.store.select(selectMeUnit);
  distancePipe: DistancePipe = new DistancePipe();
  unit: Unit = 'metric';
  isFirstItemFocused: boolean = false;

  constructor() {
    this.markerClusterGroup = L['default'].markerClusterGroup({
      iconCreateFunction: (cluster: L.MarkerCluster) => {
        return L.divIcon({
          html: `<div class="cluster">${cluster.getChildCount()}</div>`,
        });
      },
      showCoverageOnHover: false,
    });
    this.initializeMapOptions();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      combineLatest([
        this.sportStore.selectedSport$,
        this.filterStore.mapFilters$,
      ]).subscribe(([sport, filters]: [Sport, BrowseFilter[]]) => {
        this.sport = sport;
        this.filters = filters;
        this.updateVideos();
      }),
      this.mapStore.totalAvailableVideos$.subscribe((total: number) => {
        this.totalAvailableVideos = total;
      }),
      this.mapStore.isLoading$.subscribe(
        (isLoading: boolean) => (this.isLoading = isLoading)
      ),
      this.mapStore.areVideosComplete$.subscribe(
        (areComplete: boolean) => (this.isFull = areComplete)
      ),
      this.responsiveService.width$.subscribe((width: number) => {
        this.isLargeScreen = width > LARGE_SCREEN_BREAKPOINT;
      })
    );

    this.subscriptions.push(
      this.unit$.subscribe((unit: Unit) => {
        this.unit = unit;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  onMapReady(map: L.Map): void {
    this.map = map;
    this.markerClusterGroup.addTo(this.map);
    this.map.on('autopanstart', () => {
      this.isAutopanInProcess = true;
    });

    this.centerMapOnUserIfLocationShared();
    this.subscriptions.push(
      this.mapStore.videos$.subscribe((videos: VideoSection[]) => {
        this.videos = videos;
        if (!!this.mapService.selectedVideo) {
          this.mapService.setSelectedVideo(
            videos.find(
              (v: VideoSection) => v.id === this.mapService.selectedVideo.id
            )
          );
        }
        this.resetMarkers();

        if (this.path && videos.length > 0 && !this.isFirstItemFocused) {
          const latLng: L.LatLng = new L.LatLng(
            videos[0].location_start.lat,
            videos[0].location_start.lon
          );
          this.map.flyTo(latLng, CITY_ZOOM);
          this.isFirstItemFocused = true;
          this.hasMoved = true;
        }
      }),
      this.mapService.selectedVideo$.subscribe(() => {
        this.manageSelectedMarkerAndPopup();
      }),
      this.searchedPlaceStore.searchedPlace$.subscribe(
        (address: Place | null) => {
          this.moveToPlaceIfExists(address);
        }
      )
    );
  }

  async onMoveOrZoomEnd(): Promise<void> {
    if (!this.hasMoved) {
      this.hasMoved = true;
    }

    if (this.isAutopanInProcess) {
      this.isAutopanInProcess = false;
      return;
    }

    this.updateCenterAndRadius();
    this.mapService.setSelectedVideo(null);

    if (this.isMovingToAddress) {
      this.isMovingToAddress = false;
    } else {
      const searchedPlace: Place = await firstValueFrom(
        this.searchedPlaceStore.searchedPlace$
      );
      const hasCenterMoved: boolean =
        !searchedPlace ||
        this.centerAndRadius.center.lat !== searchedPlace.location.lat ||
        this.centerAndRadius.center.lng !== searchedPlace.location.lng;
      if (hasCenterMoved) {
        this.searchedPlaceStore.setSearchedPlace(null);
      }
    }
  }

  onMapClick(): void {
    this.mapService.setSelectedVideo(null);
  }

  onDisplayMore(): void {
    this.mapStore.getVideos({
      sport: this.sport,
      filters: this.filters,
      centerAndRadius: this.centerAndRadius,
      isNextPage: true,
      id: this.id,
      path: this.path,
    });
  }

  private moveToPlaceIfExists(place: Place | null): void {
    if (!place) {
      return;
    }
    if (!this.hasMoved) {
      this.hasMoved = true;
    }
    this.isMovingToAddress = true;

    let zoom: number;
    switch (place.type) {
      case 'country':
        zoom = COUNTRY_ZOOM;
        break;
      case 'region':
        zoom = REGION_ZOOM;
        break;
      case 'city':
        zoom = CITY_ZOOM;
        break;
    }
    this.map.flyTo(new L.LatLng(place.location.lat, place.location.lng), zoom);
  }

  private updateCenterAndRadius(): void {
    const center: L.LatLng = this.map.getCenter();
    const northWest: L.LatLng = this.map.getBounds().getNorthWest();
    const northEast: L.LatLng = this.map.getBounds().getNorthEast();
    const southWest: L.LatLng = this.map.getBounds().getSouthWest();
    const horizontalRadius: number = northWest.distanceTo(northEast) / 2;
    const verticalRadius: number = northWest.distanceTo(southWest) / 2;
    const radius: number =
      horizontalRadius < verticalRadius ? horizontalRadius : verticalRadius;
    this.centerAndRadius = { center, radius };
    if (!this.path) this.updateVideos();
  }

  private updateVideos(): void {
    this.mapStore.getVideos({
      sport: this.sport,
      filters: this.filters,
      centerAndRadius: this.centerAndRadius,
      isNextPage: false,
      id: this.id,
      path: this.path,
    });
  }

  private initializeMapOptions(): void {
    this.mapOptions = {
      layers: [
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          noWrap: true,
        }),
      ],
      zoom: DEFAULT_ZOOM,
      minZoom: MIN_ZOOM,
      center: L.latLng(FRANCE_COORDS.lat, FRANCE_COORDS.lng),
      maxBounds: new L.LatLngBounds(L.latLng(-90, -180), L.latLng(90, 180)),
      doubleClickZoom: false,
    };
  }

  private centerMapOnUserIfLocationShared() {
    navigator.geolocation.getCurrentPosition(
      (pos: GeolocationPosition) => {
        if (!this.hasMoved) {
          const { latitude, longitude }: GeolocationCoordinates = pos.coords;
          this.map.flyTo(new L.LatLng(latitude, longitude));
        }
      },
      () => {
        if (!this.hasMoved) {
          this.updateCenterAndRadius();
        }
      }
    );
  }

  private resetMarkers(): void {
    for (const key in this.markers) {
      this.markerClusterGroup.removeLayer(this.markers[key]);
    }

    this.markers = {};

    this.videos = [
      ...new Map(this.videos.map((v: VideoSection) => [v.id, v])).values(),
    ];
    this.totalAvailableVideos -= this.videos.length;
    for (const video of this.videos) {
      const isTouched: boolean | undefined =
        this.mapService.touchedIds[video.id];
      const marker: L.Marker = L.marker(
        [video.location_start.lat, video.location_start.lon],
        {
          icon: new L.DivIcon({
            className: 'map-video-marker',
            html: `<div class="marker-content ${isTouched ? 'touched' : ''}">
            <span class="icon icon-video"></span>
            <span> ${this.distancePipe.transform(video.distance, this.unit)}</span>
          </div>`,
            popupAnchor: [0, -10],
          }),
        }
      );
      this.setMarkerZIndex(marker, 0);
      this.markerClusterGroup.addLayer(marker);
      this.markers[video.id] = marker;
      marker.on('click', () => {
        if (this.mapService.selectedVideo === video) {
          this.mapService.setSelectedVideo(null);
        } else {
          this.mapService.setSelectedVideo(video);
        }
      });
    }

    this.manageSelectedMarkerAndPopup();
  }

  private manageSelectedMarkerAndPopup(): void {
    this.map.closePopup();
    this.selectedMarker?.unbindPopup();
    this.unselectMarker();

    const video: VideoSection = this.mapService.selectedVideo;
    if (!video) {
      return;
    }

    const marker: L.Marker = this.markers[video.id];
    if (this.isLargeScreen) {
      const popupEl: HTMLElement = this.createPopupEl();
      marker.bindPopup(popupEl, {
        className: 'video-card-popup',
        closeButton: false,
      });
      marker.openPopup();
    }

    this.selectMarker(marker);
  }

  private createPopupEl(): HTMLElement {
    const componentRef: ComponentRef<ScenicCardComponent> = createComponent(
      ScenicCardComponent,
      {
        environmentInjector: this.environmentInjector,
      }
    );

    componentRef.setInput('information', this.mapService.selectedVideo);
    componentRef.setInput('details', true);
    componentRef.setInput('condensed', true);
    componentRef.setInput('size', 'small');
    componentRef.changeDetectorRef.detectChanges();
    return componentRef.location.nativeElement;
  }

  private selectMarker(marker: L.Marker): void {
    const markerContent: Element | null = marker
      .getElement()
      ?.getElementsByClassName('marker-content')[0];
    if (!markerContent) {
      return;
    }
    markerContent.classList.add('selected');
    markerContent.classList.add('touched');
    this.setMarkerZIndex(marker, 1);

    this.selectedMarker = marker;
  }

  private unselectMarker(): void {
    if (!this.selectedMarker) {
      return;
    }

    const markerContent: Element = this.selectedMarker
      .getElement()
      ?.getElementsByClassName('marker-content')[0];
    markerContent?.classList.remove('selected');
    this.setMarkerZIndex(this.selectedMarker, 0);

    this.selectedMarker = null;
  }

  private setMarkerZIndex(marker: L.Marker, index: number): void {
    const pos: L.Point = this.map
      .latLngToLayerPoint(marker.getLatLng())
      .round();
    marker.setZIndexOffset(index - pos.y);
  }
}
